// Standard library imports
import React, { useEffect, useState } from 'react';

// Extenal library imports
import { Form, Formik, FieldArray } from 'formik';
import { toast } from 'react-toastify';
import { UilMinusCircle, UilPlusCircle } from '@iconscout/react-unicons';
import _ from 'lodash';

// Internal module imports
import { EdgeService } from '../../../services/EdgeService';
import { useLoader } from '../../../hooks';
import Dropdown from '../../../components/Inputs/Dropdown';
import { Input, ToggleButton } from '../../../components/Inputs/Input';
import { EdgeScheduleTest } from '../../../validations/Edge/ScheduleTestValidator';
import SaveProfileTest from './SaveProfileTest';
import NewJSONEditor from '../../../components/JSONEditor/NewJSONEditor';
import ModalComponent from '../../../components/ModalComponent/ModalComponent';

// Css imports
import classes from '../../../styles/CreateDevice.module.css';
import edgeClasses from '../Edge.module.css';

const ScheduleFCRV1 = ({ edgeData, setScheduleModal, setRedirectTo, setTestLogs, setSelectedIndex, baseCommands }) => {
    const [startLoader, stopLoader] = useLoader();
    const [testOption, setTestOption] = useState([]);
    const [selectedTestOption, setSelectedTestOption] = useState({});
    const [saveTestModal, setSaveTestModal] = useState(false);
    const [fieldType, setFieldType] = useState({
        label: 'Frequency',
        value: 'frequency',
    });
    const [jsonEditorMode, setJsonEditorMode] = useState(false);
    const [updateDefaultValueEditor, setUpdateDefaultValueEditor] = useState(false);

    useEffect(() => {
        const params = {
            market: 'FCR_V1',
        };
        EdgeService.GetPredefinedTest(params, startLoader, handleGetPredefinedTestSuccess, handleError, stopLoader);
    }, []);

    const handleGetPredefinedTestSuccess = ({ data }) => {
        const temp = [];
        data?.data.map((item) => {
            temp.push({
                label: item.name,
                value: item.profile,
                isPower: item?.isPower || false,
            });
        });
        setTestOption(temp);
    };

    const handleSubmit = (values) => {
        let profiles = values.profileTest;
        if (!values.isPower) {
            profiles = values.profileTest.map((p) => ({
                [fieldType.value]: p[fieldType.value],
                seconds: p.seconds,
            }));
        }
        const payload = {
            profiles: profiles,
            edgeId: edgeData.deviceId,
            type: selectedTestOption?.label || '',
            baseCommands: profiles,
            action: 'FCRscheduledTest',
            testType: 'FCR_V1',
        };
        EdgeService.SendScheduleData(payload, startLoader, handleSendScheduleSuccess, handleError, stopLoader);
    };

    const handleSendScheduleSuccess = ({ data }) => {
        toast.success(data?.data?.status ? data?.data?.status : 'No Response From Edge.');
        setScheduleModal({ status: false, data: '' });
        if (!baseCommands) {
            setRedirectTo((prev) => ({ ...prev, status: true }));
        }
    };

    const handleError = (err) => {
        if (err && err.response) toast.error(err.response.data.message);
    };

    return (
        <div style={{ minWidth: '45vw', maxHeight: '85vh', overflowY: 'scroll' }}>
            <div>
                <Formik
                    enableReinitialize
                    initialValues={{
                        isPower: false,
                        profileTest: baseCommands?.length
                            ? baseCommands
                            : [
                                  {
                                      [fieldType.value]: '',
                                      seconds: '',
                                      power: '',
                                  },
                              ],
                    }}
                    onSubmit={handleSubmit}
                    validationSchema={() => EdgeScheduleTest(fieldType.value)}
                >
                    {({ errors, touched, values, isValidating, ...props }) => {
                        return (
                            <Form>
                                <ModalComponent isOpen={saveTestModal} setOpen={setSaveTestModal}>
                                    <SaveProfileTest profileTest={values.profileTest} setSaveTestModal={setSaveTestModal} isPower={values.isPower} market={'FCR_V1'} />
                                </ModalComponent>
                                <>
                                    <div className={edgeClasses.ScheduleTestInputContainer} style={{ alignItems: 'center' }}>
                                        <div className={classes.FieldControl2}>
                                            <label>Select Predefined Test</label>
                                            <Dropdown
                                                name="test"
                                                options={testOption}
                                                onChange={(e) => {
                                                    props.setFieldValue('profileTest', e?.value);
                                                    props.setFieldValue('isPower', e?.isPower || false);
                                                    setSelectedTestOption(e);
                                                    setUpdateDefaultValueEditor(true);
                                                }}
                                            />
                                        </div>
                                        <div className={classes.FieldControl2}>
                                            <ToggleButton values={values} label={'Power'} name={`isPower`} />
                                        </div>
                                    </div>
                                    <div className={classes.FieldControl2} style={{ textAlign: 'center', marginTop: '2vh' }}>
                                        Or
                                    </div>
                                    <div>
                                        {jsonEditorMode ? (
                                            <>
                                                <div style={{ maxHeight: '30rem', overflowY: 'scroll' }}>
                                                    <div>
                                                        <NewJSONEditor
                                                            name="profile"
                                                            defaultValue={values.profileTest}
                                                            manualDefaultValueUpdate={updateDefaultValueEditor}
                                                            setManualDefaultValueUpdate={setUpdateDefaultValueEditor}
                                                            height="30rem"
                                                            onChange={(e) => {
                                                                props.setFieldValue('profileTest', e);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    {_.values(errors.profileTest).map((e, index) => (
                                                        <div>
                                                            {_.keys(e).map((k) => (
                                                                <div className="error-msg">
                                                                    Profile Index {index}.{k} {e[k]}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        ) : (
                                            <FieldArray name="profileTest">
                                                {({ push, remove, form }) => {
                                                    const { profileTest } = form.values;
                                                    return (
                                                        <div style={{ maxHeight: '30rem', overflowY: 'scroll' }}>
                                                            <div>
                                                                {profileTest?.map((item, index) => (
                                                                    <div className={edgeClasses.ScheduleTestInputContainer} key={index}>
                                                                        {values.isPower ? (
                                                                            <>
                                                                                <div key={index + fieldType.label} className={classes.FieldControl2}>
                                                                                    <label>
                                                                                        {fieldType.label}
                                                                                        <span className="required">*</span>
                                                                                    </label>
                                                                                    <Input name={`profileTest[${index}].[${fieldType.value}]`} type="number" />
                                                                                </div>

                                                                                <div key={index + 'Power'} className={classes.FieldControl2}>
                                                                                    <label>
                                                                                        Power <span className="required">*</span>
                                                                                    </label>
                                                                                    <Input name={`profileTest[${index}].power`} type="number" />
                                                                                </div>

                                                                                <div key={index + 'Seconds'} className={classes.FieldControl2}>
                                                                                    <label>
                                                                                        Seconds <span className="required">*</span>
                                                                                    </label>
                                                                                    <Input name={`profileTest[${index}].seconds`} type="number" />
                                                                                </div>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <div key={index + fieldType.label} className={classes.FieldControl2}>
                                                                                    <label>
                                                                                        {fieldType.label}
                                                                                        <span className="required">*</span>
                                                                                    </label>
                                                                                    <Input name={`profileTest[${index}].[${fieldType.value}]`} type="number" />
                                                                                </div>

                                                                                <div key={index + 'Seconds'} className={classes.FieldControl2}>
                                                                                    <label>
                                                                                        Seconds <span className="required">*</span>
                                                                                    </label>
                                                                                    <Input name={`profileTest[${index}].seconds`} type="number" />
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <div className={classes.addInputActionWrapper}>
                                                                {profileTest.length > 1 && (
                                                                    <span onClick={() => remove(profileTest?.length - 1)}>
                                                                        <UilMinusCircle
                                                                            size={'1.5vw'}
                                                                            style={{
                                                                                color: 'var(--color-primary)',
                                                                                cursor: 'pointer',
                                                                                zIndex: '1',
                                                                            }}
                                                                        />
                                                                    </span>
                                                                )}
                                                                <span
                                                                    onClick={() =>
                                                                        push({
                                                                            [fieldType.value]: '',
                                                                            seconds: '',
                                                                        })
                                                                    }
                                                                >
                                                                    <UilPlusCircle
                                                                        size={'1.5vw'}
                                                                        style={{
                                                                            color: 'var(--color-primary)',
                                                                            cursor: 'pointer',
                                                                            zIndex: '1',
                                                                        }}
                                                                    />
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                }}
                                            </FieldArray>
                                        )}
                                    </div>
                                </>
                                <div className={edgeClasses.HistoryContainer}>
                                    <>
                                        <div className={edgeClasses.HistoryLink} onClick={() => setJsonEditorMode((mode) => !mode)}>
                                            <p>Switch to {jsonEditorMode ? 'Input Editor' : 'JSON Editor'}</p>
                                        </div>
                                        <div
                                            className={edgeClasses.HistoryLink}
                                            onClick={() => {
                                                setTestLogs({
                                                    status: true,
                                                    deviceId: edgeData.deviceId,
                                                    market: edgeData.market,
                                                    edgeVersion: edgeData.edgeVersion,
                                                    testType: 'FCR_V1',
                                                });
                                                setSelectedIndex(5);
                                                setScheduleModal({ status: false, data: '' });
                                            }}
                                        >
                                            <p>History</p>
                                        </div>
                                    </>
                                    <div className={classes.ButtonContainer}>
                                        <div>
                                            <button type="button" className="btn-secondary" onClick={() => setScheduleModal({ status: false, data: '' })}>
                                                Close
                                            </button>
                                        </div>
                                        <div>
                                            <button type="button" className="btn-primary" onClick={() => setSaveTestModal({ status: true, id: '' })}>
                                                Save Test
                                            </button>
                                        </div>
                                        <button type="submit" className="btn-primary">
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </div>
    );
};

export default ScheduleFCRV1;
